import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  TextField,
  MenuItem,
  Button,
  Grid,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form } from "formik";
import * as yup from "yup";
import toast from "react-hot-toast";
import { useDropzone } from "react-dropzone";
import axiosInstant from "../../api";
import { jiraBugsEndpoint, jiraBugAttachmentEndpoint } from "../../api/endpoints";

const SEVERITIES = [
  { value: "low", label: "Low — cosmetic / minor annoyance" },
  { value: "medium", label: "Medium — something is wrong but there's a workaround" },
  { value: "high", label: "High — a feature is broken" },
  { value: "critical", label: "Critical — blocking work / data at risk" },
];

const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024;

const validationSchema = yup.object().shape({
  title: yup.string().trim().required("A short title is required").max(255, "Keep the title under 255 characters"),
  description: yup.string().trim().required("Describe what happened"),
  pageUrl: yup.string().max(2000),
});

interface BugReportModalProps {
  open: boolean;
  onClose: () => void;
}

export const BugReportModal: React.FC<BugReportModalProps> = ({ open, onClose }) => {
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!screenshot) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(screenshot);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [screenshot]);

  // Re-capture the page URL each time the modal opens
  const initialValues = {
    title: "",
    description: "",
    severity: "medium",
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/*": [] },
    maxFiles: 1,
    maxSize: MAX_SCREENSHOT_BYTES,
    onDrop: (accepted, rejected) => {
      if (rejected.length > 0) {
        toast.error("Screenshots must be images under 10MB");
        return;
      }
      if (accepted[0]) setScreenshot(accepted[0]);
    },
  });

  const handleClose = () => {
    setScreenshot(null);
    onClose();
  };

  const handleSubmit = async (values: typeof initialValues, actions: any) => {
    let created: { key: string; url: string } | null = null;
    try {
      const res = await axiosInstant.post<{ key: string; url: string }>(jiraBugsEndpoint, {
        title: values.title.trim(),
        description: values.description.trim(),
        severity: values.severity,
        pageUrl: values.pageUrl.trim(),
      });
      created = res.data;
    } catch (error: any) {
      const code = error?.response?.data;
      if (error?.response?.status === 503) {
        toast.error("Bug reporting isn't configured yet — ping the dev team directly.");
      } else if (error?.response?.status === 422) {
        toast.error(typeof code === "string" ? `Submission rejected: ${code}` : "Please check the form fields");
      } else {
        toast.error("Failed to submit the bug report. Please try again.");
      }
      actions.setSubmitting(false);
      return;
    }

    const issueLink = (
      <span>
        Bug{" "}
        <a href={created.url} target="_blank" rel="noreferrer" className="font-semibold underline">
          {created.key}
        </a>{" "}
        created
      </span>
    );

    if (screenshot) {
      try {
        const formData = new FormData();
        formData.append("file", screenshot);
        await axiosInstant.post(jiraBugAttachmentEndpoint(created.key), formData);
        toast.success(issueLink, { duration: 6000 });
      } catch {
        toast(
          <span>
            {issueLink} — but the screenshot failed to upload. You can attach it in Jira directly.
          </span>,
          { icon: "⚠️", duration: 8000 },
        );
      }
    } else {
      toast.success(issueLink, { duration: 6000 });
    }

    actions.setSubmitting(false);
    actions.resetForm();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
        <Typography
          variant="h4"
          className="font-[CircularStd]"
          sx={{ fontSize: "1.6rem", fontWeight: "bold" }}
        >
          Report a Bug
        </Typography>
        <Typography sx={{ fontSize: "0.85rem", color: "#9B958C", mt: 0.5 }}>
          Goes straight to the dev team&apos;s Jira board.
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <Box sx={{ mt: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="title"
                      label="Title*"
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                      placeholder="Short summary of the problem"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      name="description"
                      label="What happened?*"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={
                        (touched.description && errors.description) ||
                        "What you did, what you expected, and what actually happened"
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      name="severity"
                      label="Severity"
                      value={values.severity}
                      onChange={handleChange}
                    >
                      {SEVERITIES.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="pageUrl"
                      label="Page URL"
                      value={values.pageUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText="Auto-captured — edit if the problem happened elsewhere"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {screenshot ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          border: "1px solid #E5E1DB",
                          borderRadius: "9px",
                          p: 1,
                        }}
                      >
                        {preview && (
                          <Box
                            component="img"
                            src={preview}
                            alt="Screenshot preview"
                            sx={{ height: 56, width: 90, objectFit: "cover", borderRadius: "6px" }}
                          />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.8rem" }} noWrap>
                            {screenshot.name}
                          </Typography>
                          <Typography sx={{ fontSize: "0.72rem", color: "#9B958C" }}>
                            {(screenshot.size / 1024).toFixed(0)} KB
                          </Typography>
                        </Box>
                        <Button size="small" onClick={() => setScreenshot(null)}>
                          Remove
                        </Button>
                      </Box>
                    ) : (
                      <Box
                        {...getRootProps()}
                        sx={{
                          border: "1px dashed",
                          borderColor: isDragActive ? "#A58450" : "#C9C4BB",
                          borderRadius: "9px",
                          p: 2,
                          textAlign: "center",
                          cursor: "pointer",
                          color: "#9B958C",
                          fontSize: "0.82rem",
                          "&:hover": { borderColor: "#A58450" },
                        }}
                      >
                        <input {...getInputProps()} />
                        Drop a screenshot here, or click to choose one (optional)
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-start" }}>
                      <Button
                        variant="contained"
                        type="submit"
                        disabled={isSubmitting}
                        sx={{
                          bgcolor: "#A58450",
                          "&:hover": { bgcolor: "#8D703F" },
                          "&:disabled": { bgcolor: "#A58450", color: "#FFF", opacity: 0.5 },
                        }}
                      >
                        {isSubmitting ? "Submitting…" : "Submit bug"}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
